import { estimateStrength } from "./strength.js";

export interface HealthInput {
  readonly id: string;
  readonly password: string;
  readonly updatedAt: number;
}

export interface HealthReport {
  /** Entries that carry a password. */
  readonly total: number;
  readonly weakIds: ReadonlyArray<string>;
  readonly reusedIds: ReadonlyArray<string>;
  readonly oldIds: ReadonlyArray<string>;
  /** Found in known breaches (empty until the breach check has run). */
  readonly breachedIds: ReadonlyArray<string>;
  readonly issueIds: ReadonlyArray<string>;
  readonly healthyCount: number;
  /** Share of password entries without any issue, 0–100. */
  readonly score: number;
}

const STALE_AFTER_MS = 365 * 24 * 60 * 60 * 1000;

export function analyzeHealth(inputs: ReadonlyArray<HealthInput>, now: number, breached: ReadonlySet<string> = new Set<string>()): HealthReport {
  const withPassword = inputs.filter((input: HealthInput): boolean => input.password.length > 0);
  const occurrences = new Map<string, number>();
  for (const input of withPassword) {
    occurrences.set(input.password, (occurrences.get(input.password) ?? 0) + 1);
  }

  const weakIds: string[] = [];
  const reusedIds: string[] = [];
  const oldIds: string[] = [];
  const breachedIds: string[] = [];
  const issues = new Set<string>();

  for (const input of withPassword) {
    if (estimateStrength(input.password).level <= 1) {
      weakIds.push(input.id);
      issues.add(input.id);
    }
    if ((occurrences.get(input.password) ?? 0) > 1) {
      reusedIds.push(input.id);
      issues.add(input.id);
    }
    if (breached.has(input.id)) {
      breachedIds.push(input.id);
      issues.add(input.id);
    }
    if (now - input.updatedAt > STALE_AFTER_MS) {
      oldIds.push(input.id);
      issues.add(input.id);
    }
  }

  const total = withPassword.length;
  const healthyCount = total - issues.size;
  const score = total === 0 ? 100 : Math.round((healthyCount / total) * 100);
  return { total, weakIds, reusedIds, oldIds, breachedIds, issueIds: [...issues], healthyCount, score };
}
